import React from 'react';
import PropTypes from 'prop-types';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';

// PropTypes
import SharedPortalGroupProps from 'propTypes/sharedPortalGroups';

// Utilities
import {
  sharedPortalGroups as groupsDuck,
  customerInfos as customerInfosDuck,
  modals, customerGenderConfig
} from 'ducks';
import FontAwesome from 'utils/FA';
import { duckify } from 'utils/duckify';
import customerPath from 'utils/customerPath';

// Components
import LinkButton from 'components/LinkButton/LinkButton';
import ModalRoot from 'components/ModalRoot';
import Placeholder from 'components/Placeholder';
import ManageSharedPortalGroupModal from './ManageSharedPortalGroupModal';

const addItemButton = (
  <React.Fragment>
    <FontAwesome icon={faPlusCircle} postNbsp />
    Add a new Finalsite Explore Group
  </React.Fragment>
);

const ManageSharedPortalGroups = ({
  sharedPortalGroups,
  customers,
  genderConfig,
  onDestroy,
  onSubmit,
  showModal
}) => (
  <div className='manage-shared-portal-groups'>
    <Placeholder.loading ready={customers.ready && genderConfig.gender_map}>
      <ModalRoot modals={{ ManageSharedPortalGroupModal }} />
      <h2>Manage Finalsite Explore Groups</h2>
      <LinkButton
        className='add-item-button'
        content={addItemButton}
        onClick={() => showModal({ customers, onSubmit, genderConfig })}
      />

      <table className='table'>
        <thead>
          <tr>
            <th>Finalsite Explore Group Name</th>
            <th>Subdomain</th>
            <th>Location</th>
            <th>Members</th>
            <th>Last Edited By</th>
            <th>Demo</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {
            sharedPortalGroups.filter((a) => a).map(({
              id,
              name,
              location,
              subdomain,
              demo,
              custom_background_url: customBackgroundUrl,
              custom_logo_url: customLogoUrl,
              customer_ids: customerIds,
              last_edited_by: lastEditedBy,
              gender_options: genderOptions,
              ethnicity_options: ethnicityOptions,
              household_optional: householdOptional,
              ethnicity_optional: ethnicityOptional,
              gender_optional: genderOptional,
              household_hidden: householdHidden,
              ethnicity_hidden: ethnicityHidden,
              gender_hidden: genderHidden,
              display_in_branded_shared_portal: displayInBrandedSharedPortal,
              display_in_explore: displayInExplore,
              google_analytics_4_code,
            }) => (
              <tr key={id} data-group-id={id}>
                <td>{name}</td>
                <td>{subdomain}</td>
                <td>{location}</td>
                <td>
                  {
                    customerIds.map((customerId, i) => (
                      <React.Fragment key={customerId}>
                        <SchoolLink customerId={customerId} customers={customers} />
                        {(i < customerIds.length - 1) && ', '}
                      </React.Fragment>
                    ))
                  }
                </td>
                <td>{lastEditedBy}</td>
                <td>{!!demo ? 'Yes' : 'No'}</td>
                <td>
                  <LinkButton
                    content='Edit'
                    onClick={() => showModal({
                      customers,
                      customerIds,
                      id,
                      name,
                      subdomain,
                      location,
                      customBackgroundUrl,
                      customLogoUrl,
                      genderOptions,
                      genderConfig,
                      ethnicityOptions,
                      householdOptional,
                      ethnicityOptional,
                      genderOptional,
                      householdHidden,
                      ethnicityHidden,
                      genderHidden,
                      demo,
                      displayInExplore,
                      displayInBrandedSharedPortal,
                      onSubmit,
                      google_analytics_4_code
                    })}
                  />
                  &nbsp;|&nbsp;
                  <LinkButton content='Delete' onClick={() => onDestroy(id, name)} />
                </td>
              </tr>
            ))
          }
        </tbody>
      </table>
    </Placeholder.loading>
  </div>
);

const SchoolLink = ({ customers, customerId }) => {
  const { subdomain } = customers.find((customer) => (customer.id === customerId));

  return <a href={customerPath(subdomain)}>{subdomain}</a>;
};

SchoolLink.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    subdomain: PropTypes.string
  })),
  customerId: PropTypes.string
};

SchoolLink.defaultProps = {
  customers: [],
  customerId: ''
};

ManageSharedPortalGroups.propTypes = {
  sharedPortalGroups: PropTypes.arrayOf(PropTypes.shape(SharedPortalGroupProps)),
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    subdomain: PropTypes.string
  })),
  genderConfig: PropTypes.shape({
    _id: PropTypes.string,
    allow_pronouns: PropTypes.bool,
    display_pronouns: PropTypes.bool,
    errors: PropTypes.shape({}),
    gender_map: PropTypes.shape({}),
    gender_options: PropTypes.arrayOf(PropTypes.string),
    pronoun_map: PropTypes.shape({}),
    require_pronouns: PropTypes.bool
  }),

  onDestroy: PropTypes.func,
  onSubmit: PropTypes.func,
  showModal: PropTypes.func
};

ManageSharedPortalGroups.defaultProps = {
  sharedPortalGroups: [],
  customers: [],
  genderConfig: {},

  onDestroy: () => false,
  onSubmit: () => false,
  showModal: () => false
};

const mapDucksToProps = () => ({
  customers: customerInfosDuck,
  sharedPortalGroups: groupsDuck,
  genderConfig: customerGenderConfig
});

const mapDispatchToProps = (dispatch) => ({
  onDestroy: (id, name) => dispatch(
    modals.actions.show('VerificationModal', {
      title: 'Are you sure?',
      message: (
        <p>
          This will delete the group
          {` "${name}". `}
          Are you sure?
        </p>
      ),
      onConfirm: () => dispatch(groupsDuck.actions.remove({ id }))
    })
  ),
  onSubmit: (object, { setErrors, setSubmitting }) => {
    dispatch(groupsDuck.actions.save({ sharedPortalGroup: object, logoFile: object.custom_logo, backgroundFile: object.custom_background }))
      .then(() => dispatch(groupsDuck.actions.load()))
      .then(() => dispatch(modals.actions.hide()))
      .catch(({ errors }) => { setErrors(errors); setSubmitting(false); });
  },
  showModal: (props) => dispatch(
    modals.actions.show('ManageSharedPortalGroupModal', { ...props })
  )
});

export default duckify(mapDucksToProps, () => { }, mapDispatchToProps)(ManageSharedPortalGroups);
export { ManageSharedPortalGroups };
